import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Users, FileText, Receipt, X, Search, Loader2, ShieldCheck } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { EmptyState, Skeleton, Button } from "@finora/ui";
import { api } from "../../lib/api";
import { SystemApiKeySection } from "./SystemApiKeySection";
import { RestartServiceSection } from "./RestartServiceSection";

function NewCompanyModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [companyName, setCompanyName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [address, setAddress] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [lookupOk, setLookupOk] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<{ accountNumber: string; bankName: string | null }[]>([]);

  // PIB auto-popuna (04.10.2026, vlasnikov zahtev - isti n8n/CompanyWall
  // lookup kao u onboardingu i kod klijenata, vidi
  // claude/profil-firme-klijenti-pib-lookup-30-09-2026.md). Koristi se
  // POSTOJEĆI /company/lookup-pib (bez tenant ograničenja), nije pravljen
  // nov endpoint.
  const lookup = useMutation({
    mutationFn: () => api.lookupCompanyByPib(taxId),
    onSuccess: (company) => {
      if (company.name) setCompanyName(company.name);
      if (company.registrationNumber) setRegistrationNumber(company.registrationNumber);
      if (company.address) setAddress(company.address);
      setBankAccounts((company.bankAccounts ?? []).map((a) => ({ accountNumber: a.accountNumber, bankName: a.bankName })));
      setLookupOk(true);
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.createCompanyAsAdmin({
        companyName,
        taxId,
        registrationNumber,
        address,
        fullName,
        email,
        password,
        bankAccounts: bankAccounts.length ? bankAccounts : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-companies"] });
      onClose();
    },
  });

  const canSubmit =
    companyName && taxId && registrationNumber && address && fullName && email && password.length >= 6;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Nova firma</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">PIB</label>
              <div className="flex gap-2">
                <input
                  value={taxId}
                  onChange={(e) => {
                    setTaxId(e.target.value);
                    setLookupOk(false);
                  }}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => lookup.mutate()}
                  disabled={lookup.isPending || taxId.replace(/\D/g, "").length !== 9}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                  title="Pretraži po PIB-u"
                >
                  {lookup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </button>
              </div>
              {lookupOk && (
                <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                  <ShieldCheck className="h-3.5 w-3.5" /> Podaci su pronađeni i popunjeni
                </p>
              )}
              {lookupOk && bankAccounts.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  Tekući računi: {bankAccounts.map((a) => a.accountNumber).join(", ")}
                </p>
              )}
              {lookup.isError && (
                <p className="mt-1 text-xs text-red-600">
                  {lookup.error instanceof ApiError
                    ? lookup.error.response.message
                    : "Firma nije pronađena - unesite podatke ručno."}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Matični broj</label>
              <input
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Naziv firme</label>
            <input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Adresa</label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
              Nalog vlasnika firme
            </p>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Ime i prezime</label>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Početna lozinka</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Bar 6 karaktera"
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            </div>
          </div>
          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Kreiranje firme nije uspelo."}
            </p>
          )}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button className="flex-1" disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
            Sačuvaj
          </Button>
        </div>
      </div>
    </div>
  );
}

function CompanyStatsModal({
  companyId,
  companyName,
  onClose,
}: {
  companyId: string;
  companyName: string;
  onClose: () => void;
}) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["company-stats", companyId],
    queryFn: () => api.getCompanyStats(companyId),
  });

  const cards = [
    { label: "Kupci", value: stats?.customerCount, icon: Users },
    { label: "Izdate ponude", value: stats?.quoteCount, icon: FileText },
    { label: "Izdate fakture", value: stats?.invoiceCount, icon: FileText },
    { label: "Fiskalizacija", value: stats?.fiscalizedCount, icon: Receipt },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{companyName}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 px-5 py-5">
          {cards.map((card) => (
            <div key={card.label} className="rounded-xl border border-border bg-app-bg p-4">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <card.icon className="h-3.5 w-3.5" aria-hidden />
                {card.label}
              </div>
              <p className="text-2xl font-semibold text-slate-900">
                {isLoading ? "…" : (card.value ?? 0)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SuperAdminPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [statsCompany, setStatsCompany] = useState<{ id: string; name: string } | null>(null);
  const { data: companies, isLoading } = useQuery({
    queryKey: ["all-companies"],
    queryFn: () => api.listAllCompanies(),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setCompanyActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["all-companies"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">HAFiskal Admin</h1>
          <p className="text-sm text-slate-500">Sve firme registrovane na platformi</p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Nova firma
        </Button>
      </div>

      {/* Poseban red ispod naslova (ne u istom flex-u sa naslovom, vidi
          razgovor "bas bode oci") - sa 6 dugmadi u istom redu kao naslov
          (justify-between + flex bez wrap-a) dugmad su se gurala jedno preko
          drugog / redosled se lomio na užim ekranima. Ovde je sopstveni
          flex-wrap red, uvek levo poravnat, isti stil kao "Billing grupe". */}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => navigate("/finora-admin/billing")}>
          Billing grupe
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/settings/services")}>
          Settings / Services
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/settings/email")}>
          Podešavanja / Email
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/packages")}>
          Paketi
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/ponude")}>
          Ponude
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/users")}>
          Korisnici
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/print-agents")}>
          Print Agent
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/app-releases")}>
          Verzije aplikacija
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/contract-templates")}>
          Šabloni ugovora
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/backup")}>
          Bekap baze
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/earhiv-s3")}>
          E-arhiv S3
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/lpfr")}>
          L-PFR
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/hub")}>
          HA Fiscal Hub
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/wisp")}>
          WISP i SMS
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/logs")}>
          Logovi
        </Button>
        <Button variant="secondary" onClick={() => navigate("/finora-admin/podrska")}>
          Podrška (tiketi)
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !companies || companies.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={Building2} title="Nema registrovanih firmi" description="" />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Naziv</th>
                <th>PIB</th>
                <th>Korisnika</th>
                <th>Registrovana</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/finora-admin/companies/${c.id}`)}>
                  <td className="font-medium text-slate-800">{c.name}</td>
                  <td className="text-slate-500">{c.taxId}</td>
                  <td className="text-slate-500">{c.userCount}</td>
                  <td className="text-slate-500">{new Date(c.createdAt).toLocaleDateString("sr-RS")}</td>
                  <td>
                    {c.isActive ? (
                      <span className="text-xs text-emerald-600">Aktivna</span>
                    ) : (
                      <span className="text-xs text-red-500">Suspendovana</span>
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant={c.isActive ? "danger" : "secondary"}
                      disabled={toggleActive.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActive.mutate({ id: c.id, isActive: !c.isActive });
                      }}
                    >
                      {c.isActive ? "Suspenduj" : "Aktiviraj"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && <NewCompanyModal onClose={() => setModalOpen(false)} />}
      {statsCompany && (
        <CompanyStatsModal
          companyId={statsCompany.id}
          companyName={statsCompany.name}
          onClose={() => setStatsCompany(null)}
        />
      )}

      <SystemApiKeySection />
      <RestartServiceSection />
    </div>
  );
}
